import {
    Component,
    Input,
    Output,
    OnInit,
    OnDestroy,
    OnChanges,
    EventEmitter,
    SimpleChanges,
    ViewChild,
    ElementRef,
    ChangeDetectorRef
} from "@angular/core";
import {
    ITCIMarketplaceListing,
    ITCIMarketplaceV2MenuInput,
    ITCIUserInfo,
    ITCIRating,
    ITCIMediaAsset,
    ITCITenantComponentOptions,
    ITCIMarkupEditorContext,
    ITCIMarketplaceV2ActionRequest,
    SERVICE_URLS,
    TCI_MARKETPLACE_NOTIFICATION_TYPE,
    TCI_MARKETPLACE_LISTING_VIEW_MODE,
    TCI_MARKETPLACE_LISTING_EDITOR_MODE,
    TCI_MARKETPLACE_LISTING_MENU_TYPE,
    TCI_MEDIA_DISPLAY_MODE,
    TCI_MARKETPLACE_ACTIONS,
    TCI_RATINGS_COMPONENT_MODE,
    TCI_RATING_MODEL,
    TCI_ENVIRONMENT_VAR_TYPE,
    TCIMarketplaceV2DetailsMenu,
    TCIMediaDisplaySize,
    TCITenantSelectorType,
    TCI_MARKETPLACE_LISTING_TYPE
} from "@tci/common";
import {cloneDeep, isEmpty} from "lodash";
import {Observable, Subscription} from "rxjs";

import {NgUserService} from "../../ng-shared/ng-services/ng-user.service";
import {NgToastrService} from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import {NgMarketplaceV2Service} from "../ng-marketplace-v2.service";
import {NgMarketplaceV2ListingService} from "./ng-marketplace-v2-listing.service";
import {NgRatingsService} from "../../ng-shared/ng-components/ng-ratings/ng-ratings.service";
import {NgEnvVarsService} from "../../ng-shared/ng-services/ng-env-vars.service";
import {NgToolsService} from "../../ng-shared/ng-util/ng-tools.service";

// Section elements
import {
    MKP_MAIN_ITEMS,
    IMkpV2ListEvalRes,
    IMkpV2ListContext,
    MKP_ACTION_ITEMS,
    MKP_PUBLISH_ITEMS,
    MKP_FOOTER_ITEMS,
    MKP_SECTION_ITEMS
} from "./boolean-table/listing-bt-evaluations";

// Evaluation checks
import {CheckOverMainItem} from "./boolean-table/listing-bt-main";
import {CheckOverPublishItem} from "./boolean-table/listing-bt-publishing";
import {CheckOverFooterItem} from "./boolean-table/listing-bt-footer";
import {CheckOverActionItem} from "./boolean-table/listing-bt-action";

@Component({
    selector: "ng-marketplace-v2-listing",
    templateUrl: "./ng-marketplace-v2-listing.component.html",
    styleUrls: ["./ng-marketplace-v2-listing.component.less"]
})
export class NgMarketplaceV2ListingComponent implements OnInit, OnChanges, OnDestroy {
    @Input() asset?: ITCIMarketplaceListing;
    @Input() mode: TCI_MARKETPLACE_LISTING_VIEW_MODE = TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD;

    // Observe for "#installedButton" readyness
    @ViewChild("installedButton") set installedButtonRef(eleRef: ElementRef | null) {
        if (eleRef) {
            this.installedButtonEleRef = eleRef.nativeElement;
        }
    }
    // listingTypes ENUM for the template to use for showing the default icons
    LISTING_TYPE = TCI_MARKETPLACE_LISTING_TYPE;
    isSelected = false;
    private subscriptions: Subscription[] = [];

    // Mode detection
    modeIsCard = false;
    modeIsDetail = false;
    modeIsPreview = true;

    // markdown objects
    assetDescription: ITCIMarkupEditorContext;
    assetPricing: ITCIMarkupEditorContext;

    // ENUM types for the template to use
    sectionItems = MKP_SECTION_ITEMS;

    // Evaluations result map
    // key is MKP_MAIN_ITEMS | MKP_ACTION_ITEMS | MKP_PUBLISH_ITEMS | MKP_FOOTER_ITEMS
    forMain: {[key: string]: IMkpV2ListEvalRes} = {};
    forPublish: {[key: string]: IMkpV2ListEvalRes} = {};
    forFooter: {[key: string]: IMkpV2ListEvalRes} = {};
    forAction: {[key: string]: IMkpV2ListEvalRes} = {};

    // HM
    showStatusMenu = false;
    statusMenuOptions: ITCITenantComponentOptions<TCI_MARKETPLACE_LISTING_MENU_TYPE>[] = [];
    showStatusMenuPopup = false;
    statusMenuBusy = false;
    statusMenuSelectedOption: ITCITenantComponentOptions<TCI_MARKETPLACE_LISTING_MENU_TYPE>;
    statusMenuSelectedOptionDisplay: {[key: string]: boolean} = {}; // Determine what menu info to display
    modalDimensions = {width: 0, height: 0};
    showEditGuardrail = false;

    // Edit dialog
    editDialogInput: ITCIMarketplaceV2MenuInput;
    showEditDialog = false;

    // Manage access dialog
    showAccessDialog = false;

    // For ratings
    ratingModel = TCI_RATING_MODEL.Like;
    ratingsModeCard = TCI_RATINGS_COMPONENT_MODE.LIST_READ_ONLY;
    ratingsModeDetail: TCI_RATINGS_COMPONENT_MODE = TCI_RATINGS_COMPONENT_MODE.DETAIL_EDIT;
    allRatings: ITCIRating;

    // Action Menu
    @ViewChild("hmDotsActionEleRef") actionHMEleRef: ElementRef;
    actionMenu = {
        show: false,
        showPopup: false,
        selectedOption: null as MKP_ACTION_ITEMS
    };

    // After "Get" button installation or similar
    installedButtonEleRef: ElementRef;
    showAfterInstallPopup = false;

    // Media
    mediaAssets: ITCIMediaAsset[] = [];
    mediaAutoplay = true;
    mediaDisplaySize = TCIMediaDisplaySize.MEDIUM;
    mediaMode = TCI_MEDIA_DISPLAY_MODE.MODE_LIST_VIEW;
    mediaRefUrl = "";
    mediaCssClasses: string | string[] = [];

    // Tags
    tags: string;

    // User info
    userInfo: ITCIUserInfo;

    // TIBCO Cloud Integration Marketplace Program Guide
    guideUrl = "";
    contributorsAgreementUrl = SERVICE_URLS.MARKETPLACE_CONTRIBUTOR_AGREEMENT;
    contributionsAgreed = false;

    // EULA
    eulaUrl = "";
    showEULA = false;

    // IPAS-17624 - Highlight the tile if listing was updated
    isUpdated = false;

    constructor(
        private userService: NgUserService,
        private mktSvc: NgMarketplaceV2Service,
        private mktListingSvc: NgMarketplaceV2ListingService,
        private toastrSvc: NgToastrService,
        private ratingsSvc: NgRatingsService,
        private toolsSvc: NgToolsService,
        private envSvc: NgEnvVarsService,
        private ref: ChangeDetectorRef
    ) {
        this.resetSelectedStatusMenuOption();

        // Listen to updates
        this.subscriptions.push(
            mktSvc.getNotificationSubject()
                .filter(change => this.asset && change.listing.id === this.asset.id)
                .subscribe(change => {
                    Object.assign(this.asset, change.listing);
                    this.executeAfterChanges();

                    // IPAS-17624 - Make the listing tile glow on update
                    if (
                        this.mode && this.mode === TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_CARD &&
                        change.type === TCI_MARKETPLACE_NOTIFICATION_TYPE.Update
                    ) {
                        this.animateEditedTile();
                    }
                }),

            // Program Guide URL
            envSvc.getEnvVar(TCI_ENVIRONMENT_VAR_TYPE.TCI_MARKETPLACE_PROGRAM_GUIDE)
                .subscribe(vars => this.guideUrl = vars[TCI_ENVIRONMENT_VAR_TYPE.TCI_MARKETPLACE_PROGRAM_GUIDE])
        );
    }

    ngOnInit() {
        this.setMode();
        this.subscriptions.push(
            this.mktSvc.getSelectedListingSubject()
                .subscribe(asset => {
                    if (this.asset && this.modeIsCard) {
                        this.isSelected = asset && asset.id && this.asset.id === asset.id;
                    }

                    if (this.modeIsDetail &&
                        // Not selecting listing from getSelectedListingSubject when detail view in preview mode
                        (this.mode !== TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL) &&
                        !!asset &&
                        (!this.asset || this.asset.id !== asset.id)) {
                        this.asset = asset;
                        this.executeAfterChanges();
                    }
                })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.asset) {
            this.init().subscribe();
            this.executeAfterChanges();
        }
    }

    ngOnDestroy() {
        this.subscriptions
            .filter(s => s && !s.closed)
            .forEach(s => s.unsubscribe());
    }

    private executeAfterChanges() {
        this.init().subscribe();
        this.assetDescription = {content: this.asset.description};
        this.assetPricing = {content: this.asset.pricingInfo.details};
        this.mediaAssets = this.asset.mediaAssets;
        this.allRatings = this.asset.ratings || {r1_count: 0, r2_count: 0, r3_count: 0, r4_count: 0, r5_count: 0};

        // Reset checkboxes values
        this.contributionsAgreed = false;
    }

    private init(): Observable<null> {
        return this.userService.getUserInfo()
            .map(userInfo => {
                this.userInfo = userInfo;
                this.evaluate();
                this.generateStatusMenuOptions();
                this.initActionMenu();
                this.setRatingMode();
                this.getEULA();

                this.tags = (this.asset.tags || []).map(t => t.name).join(", ").trim();
                return null;
            });
    }

    private setMode() {
        this.modeIsCard = [
            TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_CARD,
            TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD
        ].includes(this.mode as any);

        this.modeIsDetail = [
            TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_DETAIL,
            TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL
        ].includes(this.mode as any);

        this.modeIsPreview = [
            TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD,
            TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL
        ].includes(this.mode as any);

        // Notify the service
        if (this.mode === TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_DETAIL) {
            this.mktListingSvc.setSelectedAsset(this.asset);
        }
    }

    private setRatingMode() {
        this.ratingsModeDetail = this.forMain[MKP_MAIN_ITEMS.RATING_EDIT].enabled ?
            TCI_RATINGS_COMPONENT_MODE.DETAIL_EDIT :
            TCI_RATINGS_COMPONENT_MODE.DETAIL_READ_ONLY;
    }

    private evaluate() {
        const context: IMkpV2ListContext = {
            item: undefined,
            asset: this.asset,
            mode: this.mode,
            userInfo: this.userInfo
        };

        // For preview some evaluations might fail
        const evaluationError = (item, e) => {
            console.error("Marketplace listing was not able to check for " + item, e);
            this.forMain[item] = {visible: false};
        };

        Object.keys(MKP_MAIN_ITEMS).forEach(item => {
            context.item = item as any;
            try {
                this.forMain[item] = CheckOverMainItem(context);
            } catch (e) {
                evaluationError(item, e);
            }
        });

        Object.keys(MKP_PUBLISH_ITEMS).forEach(item => {
            context.item = item as any;
            try {
                this.forPublish[item] = CheckOverPublishItem(context);
            } catch (e) {
                evaluationError(item, e);
            }
        });

        Object.keys(MKP_FOOTER_ITEMS).forEach(item => {
            context.item = item as any;
            try {
                this.forFooter[item] = CheckOverFooterItem(context);
            } catch (e) {
                evaluationError(item, e);
            }
        });

        Object.keys(MKP_ACTION_ITEMS).forEach(item => {
            context.item = item as any;
            try {
                this.forAction[item] = CheckOverActionItem(context);
            } catch (e) {
                evaluationError(item, e);
            }
        });
    }

    generateStatusMenuOptions() {
        if (this.forMain[MKP_MAIN_ITEMS.HM_OPTIONS].visible) {
            const statusMenuOptions = (TCIMarketplaceV2DetailsMenu.getAllMenuItems() as TCIMarketplaceV2DetailsMenu[])
                .filter(option => {
                    let evaluationElement: IMkpV2ListEvalRes;
                    if (option.isEqualTo(TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_EDIT_DETAILS)) {
                        evaluationElement = this.forMain[MKP_MAIN_ITEMS.HM_EDIT_DETAILS];
                    } else if (option.isEqualTo(TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_MANAGE_ACCESS)) {
                        evaluationElement = this.forMain[MKP_MAIN_ITEMS.HM_MANAGE_ACCESS];
                    } else if (option.isEqualTo(TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_CHANGE_VISIBILITY_PUBLIC)) {
                        evaluationElement = this.forMain[MKP_MAIN_ITEMS.HM_EDIT_UPDATE_VISIBILITY_PUBLIC];
                    } else if (option.isEqualTo(TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_CHANGE_VISIBILITY_PRIVATE)) {
                        evaluationElement = this.forMain[MKP_MAIN_ITEMS.HM_EDIT_UPDATE_VISIBILITY_PRIVATE];
                    } else if (option.isEqualTo(TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_WITHDRAW_PENDING_LISTING)) {
                        evaluationElement = this.forMain[MKP_MAIN_ITEMS.HM_WITHDRAW_PENDING_LISTING];
                    } else if (option.isEqualTo(TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_DELETE_LISTING)) {
                        evaluationElement = this.forMain[MKP_MAIN_ITEMS.HM_DELETE_LISTING];
                    } else {
                        evaluationElement = {visible: false};
                    }

                    option.setEnabled(evaluationElement.enabled);
                    return evaluationElement.visible;
                });
            this.statusMenuOptions = statusMenuOptions;

            // Hide menu if no option passed the evaluations
            if (this.statusMenuOptions.length === 0) {
                this.forMain[MKP_MAIN_ITEMS.HM_OPTIONS] = {visible: false};
                console.warn("Marketplace HM for this option is visible but no option is present");
            }
        } else {
            this.statusMenuOptions = [];
        }
    }

    toggleStatusMenu($event: MouseEvent) {
        const state = this.showStatusMenu;
        this.showStatusMenu = !state;
    }

    private resetSelectedStatusMenuOption() {
        TCIMarketplaceV2DetailsMenu.getAllMenuItems().forEach(item => {
            this.statusMenuSelectedOptionDisplay[item.getRouteId().toString()] = false;
        });
    }

    // Actions that happen after clicking a main-HM item
    statusMenuSelect(option: TCIMarketplaceV2DetailsMenu, overrideGuardrail = false) {
        this.cancelPopUp();
        this.statusMenuSelectedOption = option;

        if (option.getOptionType() === TCI_MARKETPLACE_LISTING_MENU_TYPE.POPUP) { // Opens a ng-popup pointing to the HM
            // IPAS-17314 - Moved withdraw approval popup confirmation dialog from editor to HM action.
            this.statusMenuSelectedOptionDisplay[option.getRouteId().toString()] = true;
            this.showStatusMenuPopup = true;
        } else { // Display a modal
            if (!overrideGuardrail &&
                !this.asset.enabledByDefault && // IPAS-17204 - No guardrail for OOTB
                option.getRouteId() === "details" &&
                this.forPublish[MKP_PUBLISH_ITEMS.PUBLIC].visible && // IPAS-17080 - Show guardrail if it's public
                !this.asset.isHostOrgListing // IPAS-17623 - Do not show guardrail if it's OOTB
            ) {
                this.showEditGuardrail = true;
                return;
            }

            this.modalDimensions.width = option.getModalType().getWidth();
            this.modalDimensions.height = option.getModalType().getHeight();

            switch (option.getComponentSelectorType()) { // Opens a modal
                case TCITenantSelectorType.TCI_MENU_MKP_MANAGE_ACCESS:
                    this.showAccessDialog = true;
                    break;

                case TCITenantSelectorType.TCI_MKP_EDIT_DETAILS:
                    this.editDialogInput = {
                        editorMode: TCI_MARKETPLACE_LISTING_EDITOR_MODE.EDIT,
                        listing: cloneDeep(this.asset)
                    };
                    this.showEditDialog = true;
                    break;
            }
        }
    }

    cancelPopUp() {
        this.resetSelectedStatusMenuOption();
        this.initActionMenu();
        this.showStatusMenu = false;
        this.showStatusMenuPopup = false;
        this.showEditDialog = false;
    }

    /**
     * Handles the visibility update logic
     */
    statusMenuVisibilityChange(option: TCIMarketplaceV2DetailsMenu) {
        let actionType: TCI_MARKETPLACE_ACTIONS;
        if (option.isEqualTo(TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_CHANGE_VISIBILITY_PUBLIC)) {
            actionType = TCI_MARKETPLACE_ACTIONS.PromoteVisibilityPublic;
        }
        if (option.isEqualTo(TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_CHANGE_VISIBILITY_PRIVATE)) {
            actionType = TCI_MARKETPLACE_ACTIONS.PromoteVisibilityPrivate;
        }
        // IPAS-17314 - Moved withdraw approval popup confirmation dialog from editor to HM action.
        if (option.isEqualTo(TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_WITHDRAW_PENDING_LISTING)) {
            actionType = TCI_MARKETPLACE_ACTIONS.WithdrawListing;
        }

        if (option.isEqualTo(TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_DELETE_LISTING)) {
            actionType = TCI_MARKETPLACE_ACTIONS.DeleteListing;
        }

        this.statusMenuBusy = true;
        this.actionMenuEvent(null, actionType);
        this.cancelPopUp();
    }

    // Action menu
    private initActionMenu() {
        this.actionMenu = {
            show: false,
            selectedOption: null as MKP_ACTION_ITEMS,
            showPopup: false
        };
    }

    toggleActionMenu($event: MouseEvent) {
        const state = this.actionMenu.show;
        this.actionMenu.show = !state;
    }

    /**
     * Handles action request
     */
    actionMenuEvent(selectedOption?: MKP_ACTION_ITEMS, actionType?: TCI_MARKETPLACE_ACTIONS): void {
        this.actionMenu.selectedOption = selectedOption;
        this.actionMenu.showPopup = true;

        const processAction: ITCIMarketplaceV2ActionRequest = {actionType, listing: this.asset};

        if (!!selectedOption) { // It's from template call
            switch (selectedOption) {
                case MKP_ACTION_ITEMS.UNINSTALL_LINK:
                    processAction.actionType = TCI_MARKETPLACE_ACTIONS.Uninstall;
                    break;

                default:
                    throw Error("Marketplace action is not valid: " + JSON.stringify(selectedOption));
            }
        }

        if (!!selectedOption && !!actionType) {
            throw Error("Marketplace action is not defined: " + JSON.stringify(selectedOption));
        }

        this.mktSvc.processAction(processAction)
            .do(() => this.statusMenuBusy = false)
            .flatMap(res => {
                if (res.success) {
                    if (res.data && res.data[0]) {
                        this.asset = Object.assign(this.asset, res.data[0]);

                        // IPAS-17109 - Show notification after action
                        if (selectedOption === MKP_ACTION_ITEMS.UNINSTALL_LINK) {
                            this.toastrSvc.success("The Connector has been uninstalled successfully.");
                        }
                        // IPAS-17314 - Moved withdraw approval toaster from editor to HM action.
                        if (actionType === TCI_MARKETPLACE_ACTIONS.WithdrawListing) {
                            this.toastrSvc.success(
                                `Approval request is withdrawn successfully.`,
                                `You can update visibility to be 'Public' later.`
                            ).setLife(5000);
                        }
                        return this.init().map(() => TCI_MARKETPLACE_NOTIFICATION_TYPE.Update);
                    } else if (actionType === TCI_MARKETPLACE_ACTIONS.DeleteListing) {
                        // IPAS-16887 - delete listing item
                        this.toastrSvc.success(`The listing "${this.asset.displayName}" has been deleted successfully.`);
                        return Observable.of(TCI_MARKETPLACE_NOTIFICATION_TYPE.Delete);
                    }
                } else {
                    if (!res.success && res.error.message) {
                        this.toastrSvc.error(res.error.message);
                    } else {
                        this.toastrSvc.error("Something went wrong during this request. Please try again later.");
                    }

                    console.error("Marketplace action error", res);
                    return Observable.of(null);
                }
            })
            .subscribe(type => {
                console.log("Marketplace: action type: ", type);
                if (type) {
                    this.mktSvc.notifyChange({listing: this.asset, type: type});
                }
            });
    }

    // "Get" button is removed after install action
    // "Installed" button is displayed instead and a popup is shown
    afterInstall() {
        this.init().subscribe(() => {
            this.ref.detectChanges();
            this.showAfterInstallPopup = true;
        });
    }

    private getEULA() {
        const context = {
            item: MKP_FOOTER_ITEMS.EULA_LINK,
            asset: this.asset,
            mode: this.mode,
            userInfo: this.userInfo
        };
        this.showEULA = CheckOverFooterItem(context).visible;

        if (this.showEULA) {
            this.eulaUrl = this.asset.eulaUrl;
            if (isEmpty(this.eulaUrl)) {
                this.eulaUrl = SERVICE_URLS.MARKETPLACE_EULA;
            }
        }
    }

    // IPAS-17624 - Make the listing tile glow on update
    private animateEditedTile() {
        this.isUpdated = true;
        this.toolsSvc.setTimeout(() => this.isUpdated = false, 4000);
    }
}
